import axios from "axios";
import Cookies from "js-cookie";
import {
  isEmailGateAllowedPath,
  setEmailRequiredGate,
} from "../../utils/emailGate";

// Create axios instance with base URL (из VITE_BASE_URL)
const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL || ""}/api`,
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get("whoxa_web_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

function shouldShowDebugErrorModal(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return true;

  // No HTTP response → real network / CORS / offline failure
  if (!error.response) return true;

  const status = error.response.status;
  const data = error.response.data as
    | { toast?: boolean; message?: string }
    | undefined;

  // Auth redirect is handled separately
  if (status === 401) return false;

  // API business / validation errors (this backend often uses 402)
  // toast:false means the server already marked it as non-intrusive
  if (data && data.toast === false) return false;

  // Expected client errors — don't open the debug Error modal
  if (status >= 400 && status < 500) return false;

  // 5xx and anything unexpected → debug modal
  return true;
}

// Response interceptor to handle 401 errors + unified debug alert
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      Cookies.remove("whoxa_web_token");
      Cookies.remove("whoxa_email_required");
      localStorage.clear();
      sessionStorage.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (axios.isAxiosError(error) && error.response?.status === 403) {
      const data = error.response.data as
        | { message?: string; data?: { email_required?: boolean } }
        | undefined;
      const msg = String(data?.message || "");
      if (
        msg === "email_required" ||
        data?.data?.email_required === true
      ) {
        setEmailRequiredGate(true);
        if (!isEmailGateAllowedPath(window.location.pathname)) {
          window.location.href = "/bind-email";
        }
        return Promise.reject(error);
      }
    }

    if (shouldShowDebugErrorModal(error)) {
      // Lazy import avoids circular deps with the Redux store
      import("../../utils/showAppError").then(({ showAppError }) => {
        showAppError(error);
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
